// jul18RequireAccess.jsx
// ------------------------------------------------------------------
// src/components/auth/RequireAccess.jsx
// ------------------------------------------------------------------
import React, { useEffect, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import LoadingScreen from '../LoadingScreen/LoadingScreen';
import { useAuth } from '../../contexts/AuthContext';
import { useFormContext } from '../../contexts/FormContext';
import { userCan } from '../../utils/firestore';       
import { getAuth } from 'firebase/auth';
import { getDoc, doc } from 'firebase/firestore';
import { db } from '../../firebase'; 

/**
 * Access guard:
 *   • while auth OR form context OR ACL check is loading → show spinner
 *   • if the form is public → let everyone through
 *   • if user is not signed‑in → send them to /login (keep return path)
 *   • if user is signed‑in but fails ACL → send them to /no‑access
 *   • otherwise → render children
 */
export default function RequireAccess({ children }) {
  const { currentUser, initialized, authLoading, organizationId } = useAuth();
  const { formDetails, formId, formIdReady } = useFormContext();
  const location = useLocation();
  const isBuilderRoute = location.pathname.startsWith('/form-builder');

  /* ------------------------------------------------------------
   * “Brand-new draft” flag
   *    - Set by FormContext.initializeNewFormLocally()
   *    - Cleared the first time the draft is really saved
   * ---------------------------------------------------------- */
  const isLocalDraft = localStorage.getItem(`form_${formId}_isNew`) === 'true';


  // Firebase sets auth.currentUser immediately after sign‑in,
  // while AuthContext.currentUser lags (debounce + profile fetch).
  const immediateUser = getAuth().currentUser;
  const effectiveUser = currentUser || immediateUser;

  // Debug helper — remove later if noisy
  // console.log('[RequireAccess] init:', { initialized, authLoading, currentUser, formIdReady });

  /* ──────────────────────────────────────────────────────────────
   *  ACL check (async because it calls Firestore helper)
   * ─────────────────────────────────────────────────────────── */
  const [aclChecked, setAclChecked] = useState(false);
  const [aclPass,    setAclPass]    = useState(false);

  // Reset the “ACL verdict ready?” flag any time the user lands on
  //     a **different form** or the same form in a different *mode*
  //     (builder vs. viewer).  This makes the spinner logic clearer.
  useEffect(() => {
    setAclChecked(false);
    setAclPass(false);
  }, [formId, isBuilderRoute]);

  useEffect(() => {
    // Wait until enough information is ready
    if (!formIdReady || authLoading) return;

  // Builder routes: delay until we know *who* will be the owner
  // BUT bypass this wait when we know we’re in a fresh local draft.
  if (
    isBuilderRoute &&
    formDetails?.owner === undefined &&
    !isLocalDraft            /* ← bypass */
  ) {
    return;
  }


    // 1) Public link? — always pass (no further checks)
    if (formDetails?.isPublic) {
      // ★ NEW
//      console.log('[RequireAccess] Form is public → allow');
      setAclPass(true);
      setAclChecked(true);
      return;
    }

    // 2) No signed‑in user — we’ll handle redirect later
    if (!effectiveUser) {
      setAclChecked(true);   // pass value irrelevant in this branch
      return;
    }

    // 3) Signed-in → evaluate fine-grained ACL

    // If user is signed in but orgId not ready, delay ACL eval to avoid false denials
    // (do this OUTSIDE the async IIFE so we don't return before finally runs)
    if (!organizationId) {
      return;
    }

    (async () => {
      try {
      /* --------------------------------------------------------
         Builder URLs ( /form-builder/... ) need full **edit**
         rights.  Everything else needs only **view** rights.
      -------------------------------------------------------- */
      const requiredCap = isBuilderRoute ? 'edit' : 'view';  

        let allowed = await userCan(
           requiredCap,                  
           formId,
           effectiveUser.uid,
           organizationId
         );

        /* --------------------------------------------------------
           *Conditional* builder bypass: only when the form document
           truly does **not** exist AND the current user will be the
           owner (e.g. brand-new draft / duplicate flow).
           -------------------------------------------------------- */
        if (!allowed && isBuilderRoute) {
          try {
            const snap = await getDoc(doc(db, 'forms', formId));

            /* ---------------------------------------------
             * New local draft  ➜  no Firestore doc *and*
             * the special flag we set in FormContext
             * (`form_<id>_isNew` === 'true').
             * ------------------------------------------ */
            if (!snap.exists() && isLocalDraft) {
//              console.log(
//                '[RequireAccess] Local-draft bypass → allow edit'
//              );
              allowed = true;
            } else {
//              console.log(
//                '[RequireAccess] Doc exists or not a local draft → block edit'
//              );
            }
         } catch (e) {
           /* ────────────────────────────────────────────────
            *   New local draft? Firestore will throw
            *   “permission-denied”.  That’s expected and
            *   should NOT block the builder for the creator.
            * ────────────────────────────────────────────── */
           if (
             e?.code === 'permission-denied' &&
             formDetails?.owner === effectiveUser.uid
           ) {
//             console.log(
//               '[RequireAccess] No doc + permission-denied → treat as draft, allow edit'
//             );
             allowed = true;
           } else {
             console.warn(
               '[RequireAccess] ACL denial stands – reason:',
               e
             );
           }
         }

        }


        // ★ NEW verbose log
//        console.log('[RequireAccess] ACL verdict', {
//          formId,
//          uid       : effectiveUser.uid,
//          allowed,
//        requiredCap,
//          flags     : {
//            owner      : formDetails?.owner,
//            isPublic   : formDetails?.isPublic,
//            orgAccess  : formDetails?.orgAccess,
//            sharedWith : formDetails?.sharedWith,
//            organizationId : formDetails?.organizationId,
//          },
//        });

        setAclPass(allowed);
      } catch (err) {
        console.error('[RequireAccess] ⚠️ ACL evaluation threw', err);
        setAclPass(false);
      } finally {
        setAclChecked(true);
      }
    })();

 }, [
   formIdReady,
   authLoading,
   formDetails?.isPublic,
   formDetails?.owner,
   formDetails?.orgAccess, 
   formDetails?.sharedWith,  
   formId,
   organizationId,
   effectiveUser,
   location.pathname,
   isBuilderRoute,
   isLocalDraft
 ]);

// PUBLIC FAST-PATH: if the form is public, do NOT wait on auth init or ACL
if (formIdReady && formDetails?.isPublic === true) {
  return children;
}

  /* ──────────────────────────────────────────────────────────────
   *  Wait for every dependency: auth, form, ACL
   * ─────────────────────────────────────────────────────────── */
  if (
    // NOTE: we do NOT block on `initialized` here anymore.
    // `initialized` is an app/profile hydration flag in AuthContext, and
    // can be false in incognito / empty cache even after Firebase auth is resolved.
    // Blocking on it prevents redirect-to-login for org-wide / restricted forms.

    authLoading  ||
    effectiveUser === undefined ||    // Firebase auth not resolved yet (AuthContext lag)
    !formIdReady ||                   // FormContext not ready yet
    (
      // Only block on ACL completing when we actually have a user.
      // For anonymous visitors, ACL check is irrelevant (they either view public or get redirected to login).
      !!effectiveUser && !aclChecked  // ★ keep your ACL gate for signed-in users
    )
  ) {
    console.warn('[RequireAccess] showing LoadingScreen', {
      initialized,
      authLoading,
      effectiveUserIsUndefined: effectiveUser === undefined,
      effectiveUserUid: effectiveUser?.uid ?? null,
      formIdReady,
      aclChecked,
      path: location.pathname,
    });
    return <LoadingScreen />;
  }


  /* ──────────────────────────────────────────────────────────────
   *  Unauthenticated visitor
   *    • public form? → let them in
   *    • still don’t know? → keep spinner
   *    • private form  → /login
   * ─────────────────────────────────────────────────────────── */
  if (!effectiveUser) {
    /* status not fetched yet → stay on loading screen */
    if (formDetails?.isPublic === undefined) return <LoadingScreen />;

    /* public form → allow anonymous viewing */
    if (formDetails.isPublic) return children;

    /* private form → require sign‑in */
    return (
      <Navigate
        to="/login"
        replace
        state={{
          redirectTo: location.pathname + location.search,
          reason    : 'privateResource',     // consumed by Login.jsx
        }}
      />
    );
  }


  /* ──────────────────────────────────────────────────────────────
   *  Signed‑in but NOT authorised → /no‑access
   * ─────────────────────────────────────────────────────────── */
  if (!aclPass) {
    // ★ NEW
    console.warn('[RequireAccess] Signed‑in but ACL denied → /no‑access');
    return <Navigate to="/no-access" replace state={{ from: location }} />;
  }

  /* ──────────────────────────────────────────────────────────────
   *  All good → render child route
   * ─────────────────────────────────────────────────────────── */
  return children;
}
